import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Compass, type CompassTarget } from "./components/Compass";
import { SettingsSheet } from "./components/SettingsSheet";
import { TargetPicker } from "./components/TargetPicker";
import { useGeolocation } from "./hooks/useGeolocation";
import { useHeading } from "./hooks/useHeading";
import { useWakeLock } from "./hooks/useWakeLock";
import {
  angleDelta,
  cardinal,
  distanceMeters,
  formatBearing,
  formatDistance,
  initialBearing,
  normalizeDeg,
} from "./lib/geo";
import { ALL_TARGETS_ID, type LocationId, type TargetLocation } from "./lib/locations";
import {
  loadSettings,
  resolveLocations,
  saveSettings,
  type Settings,
} from "./lib/settings";

const ALIGNED_THRESHOLD_DEG = 4;

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
}

interface AimedTarget extends TargetLocation {
  bearing: number;
  distance: number;
}

export default function App() {
  const [settings, setSettings] = useState<Settings>(() => loadSettings());
  const [started, setStarted] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const wasAlignedRef = useRef(false);

  const geo = useGeolocation(started);
  const compass = useHeading();
  useWakeLock(started);

  const locations = useMemo(() => resolveLocations(settings), [settings]);

  const update = useCallback((next: Settings) => {
    setSettings(next);
    saveSettings(next);
  }, []);

  const selectTarget = useCallback(
    (id: LocationId) => update({ ...settings, selected: id }),
    [settings, update],
  );

  useEffect(() => {
    const handler = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const position = settings.manualPosition ?? geo.position;

  const allMode = settings.selected === ALL_TARGETS_ID;

  const aimed = useMemo<AimedTarget[]>(() => {
    if (!position) return [];
    return locations.map((location) => ({
      ...location,
      bearing: initialBearing(position.lat, position.lon, location.lat, location.lon),
      distance: distanceMeters(position.lat, position.lon, location.lat, location.lon),
    }));
  }, [locations, position]);

  const selected = allMode
    ? null
    : (aimed.find((target) => target.id === settings.selected) ?? null);

  // Seçili hedef silinmişse "Tümü" görünümüne dön.
  useEffect(() => {
    if (allMode) return;
    if (locations.some((location) => location.id === settings.selected)) return;
    update({ ...settings, selected: ALL_TARGETS_ID });
  }, [allMode, locations, settings, update]);

  // iOS gerçek kuzeyi verir; Android manyetik kuzey bildirdiği için
  // kullanıcının girdiği sapma değeriyle düzeltilir.
  const trueHeading =
    compass.heading === null
      ? null
      : normalizeDeg(
          compass.heading + (compass.isTrueNorth ? 0 : settings.declination),
        );

  /** "Tümü" görünümünde ekranın baktığı yöne en yakın hedef. */
  const facing = useMemo<AimedTarget | null>(() => {
    if (!allMode || trueHeading === null || aimed.length === 0) return null;
    return aimed.reduce((closest, target) =>
      Math.abs(angleDelta(trueHeading, target.bearing)) <
      Math.abs(angleDelta(trueHeading, closest.bearing))
        ? target
        : closest,
    );
  }, [allMode, aimed, trueHeading]);

  const active = allMode ? facing : selected;

  const offset =
    active === null || trueHeading === null ? null : angleDelta(trueHeading, active.bearing);
  const aligned = offset !== null && Math.abs(offset) <= ALIGNED_THRESHOLD_DEG;

  useEffect(() => {
    if (aligned && !wasAlignedRef.current && typeof navigator !== "undefined") {
      navigator.vibrate?.(30);
    }
    wasAlignedRef.current = aligned;
  }, [aligned]);

  const compassTargets = useMemo<CompassTarget[]>(() => {
    const source = allMode ? aimed : selected ? [selected] : [];
    return source.map((target) => ({
      id: target.id,
      name: target.name,
      bearing: target.bearing,
    }));
  }, [allMode, aimed, selected]);

  const turnText =
    offset === null
      ? "—"
      : aligned
        ? "Tam karşınızda"
        : `${Math.abs(Math.round(offset))}° ${offset > 0 ? "sağa" : "sola"}`;

  let centerLabel: string;
  let centerDetail: string;
  if (allMode) {
    centerLabel = aligned && active ? active.name : "Tümü";
    if (aimed.length === 0) {
      centerDetail = "Konum bekleniyor";
    } else if (active === null) {
      centerDetail = `${aimed.length} konum işaretli`;
    } else {
      centerDetail = aligned ? "Tam karşınızda" : `${active.name} ${turnText}`;
    }
  } else {
    centerLabel = selected?.name ?? "—";
    centerDetail = selected ? turnText : "Konum bekleniyor";
  }

  const subtitle = allMode
    ? "Kırmızı bantlar kayıtlı konumların yönü"
    : (selected?.subtitle ?? "");

  const insecure =
    typeof window !== "undefined" &&
    !window.isSecureContext &&
    window.location.hostname !== "localhost";

  // Çerçeve içinde açılan sayfaya konum ve hareket sensörü izinleri, ana sayfa
  // bunları açıkça devretmediyse hiç sorulmadan reddedilir.
  const framed = typeof window !== "undefined" && window.self !== window.top;

  if (!started) {
    return (
      <main className="gate">
        <h1 className="gate__title">Nazargâh</h1>
        <blockquote className="gate__verse">
          <p>Sakın terk‑i edebden kûy‑ı mahbûb‑ı Hudâ'dır bu</p>
          <p>Nazargâh‑ı İlâhîdir makām‑ı Mustafâ'dır bu</p>
          <cite>Nâbî</cite>
        </blockquote>
        <p className="gate__text">
          Kayıtlı yerlerin yönünü gösterir. Başlamak için konum ve pusula
          sensörü izni gerekir.
        </p>
        <button
          type="button"
          className="button button--primary"
          onClick={async () => {
            await compass.requestPermission();
            setStarted(true);
          }}
        >
          Pusulayı başlat
        </button>
        {insecure && (
          <p className="gate__warn">
            Bu sayfa güvenli bağlantı (HTTPS) üzerinden açılmadığı için konum ve
            pusula sensörü çalışmayabilir.
          </p>
        )}
        {framed && (
          <p className="gate__warn">
            Sayfa bir çerçeve içinde açık. Tarayıcı bu durumda konum ve pusula
            iznini sormadan reddedebilir; sayfayı kendi sekmesinde açın.
          </p>
        )}
      </main>
    );
  }

  return (
    <main className="app">
      <header className="app__head">
        <h1>Nazargâh</h1>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => setSheetOpen(true)}
        >
          Ayarlar
        </button>
      </header>

      <TargetPicker
        locations={locations}
        selected={settings.selected}
        onSelect={selectTarget}
      />

      <Compass
        mode={allMode ? "all" : "single"}
        heading={trueHeading}
        targets={compassTargets}
        activeId={active?.id ?? null}
        label={centerLabel}
        detail={centerDetail}
        aligned={aligned}
        halfWidthDeg={ALIGNED_THRESHOLD_DEG}
      />

      <p className="app__subtitle">{subtitle}</p>

      <dl className="readout">
        <div>
          <dt>Mesafe</dt>
          <dd>{active ? formatDistance(active.distance) : "—"}</dd>
        </div>
        <div>
          <dt>Yön</dt>
          <dd>
            {active ? `${formatBearing(active.bearing)} · ${cardinal(active.bearing)}` : "—"}
          </dd>
        </div>
        <div>
          <dt>Doğruluk</dt>
          <dd>
            {settings.manualPosition
              ? "Elle girildi"
              : geo.position
                ? `± ${Math.round(geo.position.accuracy)} m`
                : "—"}
          </dd>
        </div>
      </dl>

      <div className="notices">
        {insecure && (
          <p className="notice notice--warn">
            Güvenli olmayan bağlantı: konum ve pusula sensörü engellenebilir.
          </p>
        )}

        {framed && (
          <p className="notice notice--warn">
            Sayfa bir çerçeve içinde açık; izinler sorulmadan reddedilmiş
            olabilir.{" "}
            <a
              className="link"
              href={window.location.href}
              target="_blank"
              rel="noreferrer noopener"
            >
              Kendi sekmesinde aç
            </a>
          </p>
        )}

        {geo.status === "locating" && !position && (
          <p className="notice">Konumunuz alınıyor…</p>
        )}

        {(geo.status === "denied" || geo.status === "error") && !settings.manualPosition && (
          <p className="notice notice--warn">
            {geo.message}{" "}
            <button type="button" className="link" onClick={geo.request}>
              Yeniden dene
            </button>{" "}
            ·{" "}
            <button type="button" className="link" onClick={() => setSheetOpen(true)}>
              Konumu elle gir
            </button>
          </p>
        )}

        {compass.status === "needs-permission" && (
          <p className="notice notice--warn">
            Pusula sensörü izni gerekiyor.{" "}
            <button
              type="button"
              className="link"
              onClick={() => void compass.requestPermission()}
            >
              İzin ver
            </button>
          </p>
        )}

        {compass.status === "denied" && (
          <p className="notice notice--warn">
            Pusula sensörüne izin verilmedi. Kadran kuzey yukarı modunda
            çalışıyor: telefonun üstünü coğrafi kuzeye çevirin.
          </p>
        )}

        {compass.status === "unsupported" && (
          <p className="notice">
            Bu cihaz pusula sensörü vermiyor. Kadran kuzey yukarı modunda: ok,
            kuzeye göre hedefin yönünü gösterir.
          </p>
        )}

        {compass.needsCalibration && (
          <p className="notice notice--warn">
            Pusula kalibrasyon istiyor: telefonu havada 8 çizerek birkaç saniye
            hareket ettirin.
          </p>
        )}

        {compass.status === "ready" && !compass.isTrueNorth && settings.declination === 0 && (
          <p className="notice">
            Cihaz manyetik kuzeyi bildiriyor. Daha kesin sonuç için Ayarlar'dan
            manyetik sapma değerini girin.
          </p>
        )}

        {installPrompt && (
          <p className="notice">
            <button
              type="button"
              className="link"
              onClick={async () => {
                await installPrompt.prompt();
                setInstallPrompt(null);
              }}
            >
              Uygulamayı ana ekrana ekle
            </button>
          </p>
        )}
      </div>

      <footer className="app__foot">
        {active ? (
          <>
            <a
              className="link"
              href={`https://www.google.com/maps/search/?api=1&query=${active.lat},${active.lon}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              {active.name} haritada
            </a>
            <span className="app__coords">
              {active.lat.toFixed(4)}, {active.lon.toFixed(4)}
            </span>
          </>
        ) : (
          <span className="app__coords">{locations.length} konum kayıtlı</span>
        )}
      </footer>

      <SettingsSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        locations={locations}
        settings={settings}
        onChange={update}
        ownPosition={geo.position}
      />
    </main>
  );
}
